// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { normalizeErrorString } from '@medplum/core';
import type { Encounter, Organization, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { JSX, ReactNode } from 'react';
import { useOrganization } from './OrganizationContext';
import { getAINotesSettings, type AINotesSettingsData } from '../utils/organizationSettings';
import { showErrorNotification, showSuccessNotification } from '../utils/notifications';
import { getTenantConfig } from '../config/tenant';

/**
 * Resample audio from source sample rate to target sample rate using linear interpolation.
 * AWS Transcribe Medical works best with 16kHz audio.
 */
function resampleAudio(samples: Float32Array, sourceSampleRate: number, targetSampleRate: number): Float32Array {
  if (sourceSampleRate === targetSampleRate) {
    return samples;
  }

  const ratio = sourceSampleRate / targetSampleRate;
  const newLength = Math.floor(samples.length / ratio);
  const result = new Float32Array(newLength);

  for (let i = 0; i < newLength; i++) {
    const srcIndex = i * ratio;
    const srcIndexFloor = Math.floor(srcIndex);
    const srcIndexCeil = Math.min(srcIndexFloor + 1, samples.length - 1);
    const fraction = srcIndex - srcIndexFloor;

    // Linear interpolation between samples
    result[i] = samples[srcIndexFloor] * (1 - fraction) + samples[srcIndexCeil] * fraction;
  }

  console.log(`Resampled audio from ${sourceSampleRate}Hz to ${targetSampleRate}Hz (${samples.length} -> ${newLength} samples)`);
  return result;
}

/**
 * Create a WAV file from raw PCM Float32 samples.
 * This creates a proper WAV file with headers that AWS Transcribe Medical can process.
 */
function createWavFromSamples(samples: Float32Array, sampleRate: number): Blob {
  const format = 1; // PCM
  const bitDepth = 16;
  const numberOfChannels = 1;
  const bytesPerSample = bitDepth / 8;
  const blockAlign = numberOfChannels * bytesPerSample;
  const dataLength = samples.length * bytesPerSample;
  const bufferLength = 44 + dataLength;

  const arrayBuffer = new ArrayBuffer(bufferLength);
  const view = new DataView(arrayBuffer);

  // WAV header - RIFF chunk descriptor
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataLength, true); // ChunkSize = 36 + SubChunk2Size
  writeString(view, 8, 'WAVE');

  // fmt sub-chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // Subchunk1Size (16 for PCM)
  view.setUint16(20, format, true); // AudioFormat (1 = PCM)
  view.setUint16(22, numberOfChannels, true); // NumChannels
  view.setUint32(24, sampleRate, true); // SampleRate
  view.setUint32(28, sampleRate * blockAlign, true); // ByteRate = SampleRate * NumChannels * BitsPerSample/8
  view.setUint16(32, blockAlign, true); // BlockAlign = NumChannels * BitsPerSample/8
  view.setUint16(34, bitDepth, true); // BitsPerSample

  // data sub-chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataLength, true); // Subchunk2Size = NumSamples * NumChannels * BitsPerSample/8

  // Write audio data - convert Float32 [-1, 1] to Int16 [-32768, 32767]
  let offset = 44;
  for (const sample of samples) {
    // Clamp to [-1, 1] range
    const clamped = Math.max(-1, Math.min(1, sample));
    // Convert to 16-bit signed integer and round
    const int16 = Math.round(clamped * 32767);
    view.setInt16(offset, int16, true);
    offset += 2;
  }

  console.log('WAV file created:', {
    sampleRate,
    samples: samples.length,
    duration: (samples.length / sampleRate).toFixed(2) + 's',
    fileSize: bufferLength,
  });

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

function writeString(view: DataView, offset: number, string: string): void {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

export type RecordingState = 'idle' | 'recording' | 'paused' | 'processing' | 'transcribing';

export interface RecordingSession {
  /** The encounter being recorded */
  encounter: Encounter;
  /** The patient for display purposes */
  patient?: Patient;
  /** Recording state */
  state: RecordingState;
  /** Recording duration in milliseconds */
  durationMs: number;
  /** Current audio level (0-100) */
  audioLevel: number;
  /** Error message if any */
  error: string | null;
  /** Status message during processing */
  statusMessage: string;
}

export type TranscriptionJobStatus = 'processing' | 'completed' | 'failed';

export interface TranscriptionJob {
  /** AWS Transcribe job name */
  jobName: string;
  /** Encounter ID this job is for */
  encounterId: string;
  /** Patient ID for display purposes */
  patientId?: string;
  /** Patient display name */
  patientName?: string;
  /** S3 key of the audio file */
  audioS3Key: string;
  /** Note output format */
  noteOutputFormat: string;
  /** Job status */
  status: TranscriptionJobStatus;
  /** Status message */
  statusMessage: string;
  /** Start time of the job */
  startTime: number;
  /** Error message if failed */
  error?: string;
  /** Transcription result when completed */
  transcription?: string;
  /** Generated note when completed */
  generatedNote?: string;
}

interface AIRecordingContextValue {
  /** Current recording session, null if not recording */
  session: RecordingSession | null;
  /** AI Notes settings for the current organization */
  aiSettings: AINotesSettingsData | null;
  /** Whether the browser supports audio recording */
  isSupported: boolean;
  /** Start a new recording session */
  startRecording: (encounter: Encounter, patient?: Patient) => Promise<void>;
  /** Stop recording and process */
  stopRecording: () => Promise<void>;
  /** Pause recording */
  pauseRecording: () => void;
  /** Resume recording */
  resumeRecording: () => void;
  /** Cancel recording without saving */
  cancelRecording: () => void;
  /** Check if we can open a new encounter (blocked if recording different encounter) */
  canOpenEncounter: (encounterId: string) => boolean;
  /** Callback to receive the transcription result */
  onTranscriptionComplete?: (transcription: string, generatedNote: string) => void;
  /** Set the callback for transcription completion */
  setOnTranscriptionComplete: (callback: ((transcription: string, generatedNote: string) => void) | undefined) => void;
  /** Background transcription jobs (keyed by encounter ID) */
  transcriptionJobs: Map<string, TranscriptionJob>;
  /** Get transcription job for a specific encounter */
  getTranscriptionJob: (encounterId: string) => TranscriptionJob | undefined;
  /** Dismiss/acknowledge a completed job */
  dismissTranscriptionJob: (encounterId: string) => void;
}

const AIRecordingContext = createContext<AIRecordingContextValue | undefined>(undefined);

interface AIRecordingProviderProps {
  children: ReactNode;
}

export function AIRecordingProvider({ children }: AIRecordingProviderProps): JSX.Element {
  const medplum = useMedplum();
  const { currentOrganization } = useOrganization();
  const [session, setSession] = useState<RecordingSession | null>(null);
  const [aiSettings, setAiSettings] = useState<AINotesSettingsData | null>(null);
  const [transcriptionJobs, setTranscriptionJobs] = useState<Map<string, TranscriptionJob>>(new Map());
  const onTranscriptionCompleteRef = useRef<((transcription: string, generatedNote: string) => void) | undefined>(undefined);
  const pollingIntervalsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  // Refs for recording
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const pcmDataRef = useRef<Float32Array[]>([]); // Store raw PCM samples
  const isRecordingRef = useRef<boolean>(false);
  const startTimeRef = useRef<number>(0);
  const pausedDurationRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const levelTimerRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(null);

  const isSupported = typeof navigator !== 'undefined' &&
    'mediaDevices' in navigator &&
    'getUserMedia' in navigator.mediaDevices;

  // Load AI Notes settings when organization changes
  useEffect(() => {
    if (currentOrganization?.id) {
      medplum
        .readResource('Organization', currentOrganization.id)
        .then((org: Organization) => {
          const settings = getAINotesSettings(org);
          setAiSettings(settings);
        })
        .catch(console.error);
    }
  }, [currentOrganization?.id, medplum]);

  // Cleanup function
  const cleanupRecording = useCallback(() => {
    isRecordingRef.current = false;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (levelTimerRef.current) {
      cancelAnimationFrame(levelTimerRef.current);
      levelTimerRef.current = null;
    }
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    pcmDataRef.current = [];
  }, []);

  // Update audio level
  const updateAudioLevel = useCallback(() => {
    if (!analyserRef.current || session?.state !== 'recording') {
      return;
    }

    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteFrequencyData(dataArray);

    let sum = 0;
    for (let i = 0; i < dataArray.length; i++) {
      sum += dataArray[i] * dataArray[i];
    }
    const rms = Math.sqrt(sum / dataArray.length);
    const level = Math.min(100, Math.round((rms / 255) * 100 * 2));

    setSession(prev => prev ? { ...prev, audioLevel: level } : null);

    levelTimerRef.current = requestAnimationFrame(updateAudioLevel);
  }, [session?.state]);

  // Start recording
  const startRecording = useCallback(async (encounter: Encounter, patient?: Patient) => {
    if (!isSupported) {
      showErrorNotification('Audio recording is not supported in this browser');
      return;
    }

    if (session) {
      showErrorNotification('A recording is already in progress');
      return;
    }

    try {
      // Initialize session
      setSession({
        encounter,
        patient,
        state: 'processing',
        durationMs: 0,
        audioLevel: 0,
        error: null,
        statusMessage: 'Requesting microphone access...',
      });

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: { ideal: 16000 },
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;

      // Set up audio context for recording and level monitoring
      // Note: Chrome may ignore the sampleRate option, so we'll use whatever rate it gives us
      // AWS Transcribe Medical supports various sample rates (8000-48000 Hz)
      audioContextRef.current = new AudioContext();

      /*
       * Resume before capturing anything.
       *
       * Chrome starts an AudioContext suspended unless the page holds user
       * activation, and a suspended context never fires onaudioprocess — so
       * the recorder captures nothing while appearing to run, the timer counts
       * up, and the only sign is "Recording too short" at the end of a long
       * consultation. Resuming is a no-op when it is already running.
       */
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      if (audioContextRef.current.state !== 'running') {
        /*
         * Failing here loses nothing. Failing at the end loses the recording,
         * and the clinician has already had the conversation.
         */
        throw new Error(
          'The browser would not start audio capture. Click anywhere on the page and start the recording again.'
        );
      }

      const actualSampleRate = audioContextRef.current.sampleRate;
      console.log('AudioContext sample rate:', actualSampleRate, 'Hz');
      const source = audioContextRef.current.createMediaStreamSource(stream);

      // Set up analyser for audio level monitoring
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;
      source.connect(analyserRef.current);

      // Set up ScriptProcessorNode for direct PCM capture
      // This bypasses codec issues by recording raw audio samples
      const bufferSize = 4096;
      scriptProcessorRef.current = audioContextRef.current.createScriptProcessor(bufferSize, 1, 1);
      pcmDataRef.current = [];
      isRecordingRef.current = true;

      scriptProcessorRef.current.onaudioprocess = (e) => {
        if (isRecordingRef.current) {
          // Get the input audio data (Float32Array)
          const inputData = e.inputBuffer.getChannelData(0);
          // Make a copy since the buffer will be reused
          pcmDataRef.current.push(new Float32Array(inputData));
        }
      };

      // Connect: source -> scriptProcessor -> destination (required for it to work)
      source.connect(scriptProcessorRef.current);
      scriptProcessorRef.current.connect(audioContextRef.current.destination);

      console.log('Recording PCM audio at', actualSampleRate, 'Hz');

      startTimeRef.current = Date.now();
      pausedDurationRef.current = 0;

      setSession(prev => prev ? {
        ...prev,
        state: 'recording',
        statusMessage: '',
      } : null);

      // Start duration timer
      timerRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current - pausedDurationRef.current;
        setSession(prev => prev ? { ...prev, durationMs: elapsed } : null);

        // Check max duration
        const maxMs = (aiSettings?.maxRecordingMinutes || 30) * 60 * 1000;
        if (elapsed >= maxMs) {
          showErrorNotification('Maximum recording duration reached');
          stopRecording();
        }
      }, 100);

      // Start audio level monitoring
      updateAudioLevel();

    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to start recording');
      setSession(prev => prev ? { ...prev, state: 'idle', error: error.message } : null);
      showErrorNotification(error.message);
      cleanupRecording();
      setSession(null);
    }
  }, [isSupported, session, aiSettings?.maxRecordingMinutes, cleanupRecording, updateAudioLevel]);

  // Stop recording and process
  const stopRecording = useCallback(async () => {
    if (!session || session.state === 'idle') {
      return;
    }

    setSession(prev => prev ? { ...prev, state: 'processing', statusMessage: 'Stopping recording...' } : null);

    // Stop collecting PCM data
    isRecordingRef.current = false;

    // Get the sample rate before cleanup
    const sampleRate = audioContextRef.current?.sampleRate || 16000;

    // Combine all PCM chunks into a single Float32Array
    const totalLength = pcmDataRef.current.reduce((acc, chunk) => acc + chunk.length, 0);
    const combinedSamples = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of pcmDataRef.current) {
      combinedSamples.set(chunk, offset);
      offset += chunk.length;
    }

    console.log('Recorded PCM samples:', combinedSamples.length, 'at', sampleRate, 'Hz');
    console.log('Recording duration:', (combinedSamples.length / sampleRate).toFixed(2), 'seconds');

    cleanupRecording();

    // Now process the recording
    setSession(prev => prev ? { ...prev, state: 'transcribing', statusMessage: 'Creating audio file...' } : null);

    try {
      // Resample to 16kHz for optimal AWS Transcribe Medical compatibility
      // Chrome typically records at 48kHz which is at the upper limit
      const targetSampleRate = 16000;
      const resampledSamples = resampleAudio(combinedSamples, sampleRate, targetSampleRate);

      // Create WAV file from resampled PCM samples
      const wavBlob = createWavFromSamples(resampledSamples, targetSampleRate);
      console.log('Created WAV file, size:', wavBlob.size, 'bytes');

      // Check minimum duration (AWS Transcribe requires at least a few seconds)
      const durationSeconds = resampledSamples.length / targetSampleRate;
      if (resampledSamples.length === 0) {
        /*
         * Distinct from a short recording, and nothing like it. No samples at
         * all means capture never ran — a suspended AudioContext, a
         * backgrounded tab, a microphone taken by something else — and telling
         * someone who spoke for ten minutes to "record at least 1 second" sends
         * them to fix the one thing that was not wrong.
         */
        throw new Error(
          'No audio was captured. The microphone may be in use by another window, or this tab was in the background while recording.'
        );
      }
      if (durationSeconds < 1) {
        throw new Error('Recording too short. Please record at least 1 second of audio.');
      }

      setSession(prev => prev ? { ...prev, statusMessage: 'Getting upload URL...' } : null);

      // Step 1: Get presigned URL for direct S3 upload
      const tenantConfig = getTenantConfig();
      const presignedResult = await medplum.executeBot(
        { system: tenantConfig.botIdentifierSystem, value: 'ai-notes-presigned-url' },
        {
          encounterId: session.encounter.id,
          contentType: 'audio/wav',
          extension: 'wav',
        },
        'application/json'
      );

      if (!presignedResult.success || !presignedResult.uploadUrl || !presignedResult.s3Key) {
        throw new Error(presignedResult.error || 'Failed to get upload URL');
      }

      console.log('Got presigned URL for S3 key:', presignedResult.s3Key);

      setSession(prev => prev ? { ...prev, statusMessage: 'Uploading audio directly to S3...' } : null);

      // Step 2: Upload directly to S3 using presigned URL (bypasses Medplum binary storage)
      const uploadResponse = await fetch(presignedResult.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'audio/wav',
        },
        body: wavBlob,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload audio to S3: ${uploadResponse.status} ${uploadResponse.statusText}`);
      }

      console.log('Audio uploaded directly to S3 successfully');

      setSession(prev => prev ? { ...prev, statusMessage: 'Starting transcription...' } : null);

      // Step 3: Execute the AI Notes transcribe bot to START the transcription job
      const startResult = await medplum.executeBot(
        { system: tenantConfig.botIdentifierSystem, value: 'ai-notes-transcribe' },
        {
          s3Key: presignedResult.s3Key,
          audioContentType: 'audio/wav',
          encounterId: session.encounter.id,
          organizationId: currentOrganization?.id,
          medicalSpecialty: aiSettings?.medicalSpecialty || 'PRIMARYCARE',
          transcriptionMode: 'batch',
          noteOutputFormat: aiSettings?.noteOutputFormat || 'soap',
          languageCode: aiSettings?.languageCode || 'en-US',
          enableSpeakerIdentification: aiSettings?.enableSpeakerIdentification ?? true,
          autoExtractEntities: aiSettings?.autoExtractEntities ?? true,
          customVocabularyName: aiSettings?.customVocabularyName || undefined,
        },
        'application/json'
      );

      if (!startResult.success || !startResult.jobName) {
        throw new Error(startResult.error || 'Failed to start transcription');
      }

      console.log('Transcription job started:', startResult.jobName);

      // Step 4: Create background job and start polling in the background
      const encounterId = session.encounter.id || '';
      const patientId = session.patient?.id;
      const patientName = session.patient?.name?.[0]
        ? `${session.patient.name[0].given?.join(' ') || ''} ${session.patient.name[0].family || ''}`.trim()
        : undefined;

      const newJob: TranscriptionJob = {
        jobName: startResult.jobName,
        encounterId,
        patientId,
        patientName,
        audioS3Key: presignedResult.s3Key,
        noteOutputFormat: aiSettings?.noteOutputFormat || 'soap',
        status: 'processing',
        statusMessage: 'Transcribing audio...',
        startTime: Date.now(),
      };

      // Add job to state
      setTranscriptionJobs(prev => {
        const updated = new Map(prev);
        updated.set(encounterId, newJob);
        return updated;
      });

      // Clear the recording session immediately - UI will now show the background job status
      setSession(null);
      showSuccessNotification('Recording submitted for transcription. You can continue working.');

      // Start background polling for this job
      startBackgroundPolling(encounterId, startResult.jobName, presignedResult.s3Key, aiSettings?.noteOutputFormat || 'soap');

    } catch (err) {
      showErrorNotification(normalizeErrorString(err));
      setSession(null);
    }
  }, [session, cleanupRecording, medplum, currentOrganization?.id, aiSettings]);

  // Background polling function for transcription status
  const startBackgroundPolling = useCallback((
    encounterId: string,
    jobName: string,
    audioS3Key: string,
    noteOutputFormat: string
  ) => {
    // Clear any existing polling for this encounter
    const existingInterval = pollingIntervalsRef.current.get(encounterId);
    if (existingInterval) {
      clearInterval(existingInterval);
    }

    let attempts = 0;
    const maxAttempts = 60; // 5 minutes max

    const pollStatus = async (): Promise<void> => {
      try {
        const tenantConfig = getTenantConfig();
        const statusResult = await medplum.executeBot(
          { system: tenantConfig.botIdentifierSystem, value: 'ai-notes-status' },
          {
            jobName,
            audioS3Key,
            noteOutputFormat,
          },
          'application/json'
        );

        console.log(`[Background] Status check for ${encounterId}:`, statusResult.status);

        if (statusResult.status === 'COMPLETED') {
          // Stop polling
          const interval = pollingIntervalsRef.current.get(encounterId);
          if (interval) {
            clearInterval(interval);
            pollingIntervalsRef.current.delete(encounterId);
          }

          // Update job status
          setTranscriptionJobs(prev => {
            const updated = new Map(prev);
            const job = updated.get(encounterId);
            if (job) {
              updated.set(encounterId, {
                ...job,
                status: 'completed',
                statusMessage: 'AI Notes ready for review',
                transcription: statusResult.transcription,
                generatedNote: statusResult.generatedNote,
              });
            }
            return updated;
          });

          // Show notification
          showSuccessNotification('AI Notes are ready for review!');

          // Create a Task resource for the provider to review
          await createReviewTask(encounterId, statusResult.transcription, statusResult.generatedNote);

        } else if (statusResult.status === 'FAILED') {
          // Stop polling
          const interval = pollingIntervalsRef.current.get(encounterId);
          if (interval) {
            clearInterval(interval);
            pollingIntervalsRef.current.delete(encounterId);
          }

          // Update job status
          setTranscriptionJobs(prev => {
            const updated = new Map(prev);
            const job = updated.get(encounterId);
            if (job) {
              updated.set(encounterId, {
                ...job,
                status: 'failed',
                statusMessage: 'Transcription failed',
                error: statusResult.error || 'Unknown error',
              });
            }
            return updated;
          });

          showErrorNotification(`Transcription failed: ${statusResult.error || 'Unknown error'}`);
        } else {
          // Still processing - update elapsed time
          attempts++;
          const elapsed = Math.floor((Date.now() - (transcriptionJobs.get(encounterId)?.startTime || Date.now())) / 1000);
          const minutes = Math.floor(elapsed / 60);
          const seconds = elapsed % 60;

          setTranscriptionJobs(prev => {
            const updated = new Map(prev);
            const job = updated.get(encounterId);
            if (job) {
              updated.set(encounterId, {
                ...job,
                statusMessage: `Transcribing audio (${minutes}:${String(seconds).padStart(2, '0')} elapsed)...`,
              });
            }
            return updated;
          });

          if (attempts >= maxAttempts) {
            // Timeout
            const interval = pollingIntervalsRef.current.get(encounterId);
            if (interval) {
              clearInterval(interval);
              pollingIntervalsRef.current.delete(encounterId);
            }

            setTranscriptionJobs(prev => {
              const updated = new Map(prev);
              const job = updated.get(encounterId);
              if (job) {
                updated.set(encounterId, {
                  ...job,
                  status: 'failed',
                  statusMessage: 'Transcription timed out',
                  error: 'Transcription timed out after 5 minutes',
                });
              }
              return updated;
            });

            showErrorNotification('Transcription timed out after 5 minutes');
          }
        }
      } catch (err) {
        console.error('Error polling transcription status:', err);
        // Don't stop polling on transient errors, just log them
      }
    };

    // Start polling every 5 seconds
    const intervalId = setInterval(pollStatus, 5000);
    pollingIntervalsRef.current.set(encounterId, intervalId);

    // Also poll immediately
    pollStatus();
  }, [medplum, transcriptionJobs]);

  // Create a Task resource for the provider to review the notes
  const createReviewTask = useCallback(async (
    encounterId: string,
    transcription?: string,
    generatedNote?: string
  ): Promise<void> => {
    try {
      const job = transcriptionJobs.get(encounterId);
      if (!job) return;

      // Create a Task for reviewing the AI-generated notes
      const taskTenantConfig = getTenantConfig();
      const taskTypeSystem = `https://${taskTenantConfig.domain}/task-type`;
      const taskInputTypeSystem = `https://${taskTenantConfig.domain}/task-input-type`;

      await medplum.createResource({
        resourceType: 'Task',
        status: 'requested',
        intent: 'order',
        priority: 'routine',
        code: {
          coding: [{
            system: taskTypeSystem,
            code: 'review-ai-notes',
            display: 'Review AI-Generated Notes',
          }],
          text: 'Review AI-Generated Notes',
        },
        description: `AI-generated clinical notes are ready for review${job.patientName ? ` for patient ${job.patientName}` : ''}.`,
        focus: {
          reference: `Encounter/${encounterId}`,
        },
        for: job.patientId ? {
          reference: `Patient/${job.patientId}`,
        } : undefined,
        authoredOn: new Date().toISOString(),
        input: [
          {
            type: {
              coding: [{
                system: taskInputTypeSystem,
                code: 'transcription',
                display: 'Transcription',
              }],
            },
            valueString: transcription || '',
          },
          {
            type: {
              coding: [{
                system: taskInputTypeSystem,
                code: 'generated-note',
                display: 'Generated Note',
              }],
            },
            valueString: generatedNote || '',
          },
        ],
      });

      console.log('Created review task for encounter:', encounterId);
    } catch (err) {
      console.error('Failed to create review task:', err);
    }
  }, [medplum, transcriptionJobs]);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (session?.state === 'recording') {
      // Stop collecting PCM data
      isRecordingRef.current = false;
      pausedDurationRef.current = Date.now() - startTimeRef.current - (session?.durationMs || 0);

      if (levelTimerRef.current) {
        cancelAnimationFrame(levelTimerRef.current);
        levelTimerRef.current = null;
      }

      setSession(prev => prev ? { ...prev, state: 'paused', audioLevel: 0 } : null);
    }
  }, [session?.state, session?.durationMs]);

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (session?.state === 'paused') {
      // Resume collecting PCM data
      isRecordingRef.current = true;
      startTimeRef.current = Date.now() - (session?.durationMs || 0);

      setSession(prev => prev ? { ...prev, state: 'recording' } : null);
      updateAudioLevel();
    }
  }, [session?.state, session?.durationMs, updateAudioLevel]);

  // Cancel recording
  const cancelRecording = useCallback(() => {
    cleanupRecording();
    setSession(null);
  }, [cleanupRecording]);

  // Check if we can open an encounter
  const canOpenEncounter = useCallback((encounterId: string): boolean => {
    // If not recording, can open any encounter
    if (!session || session.state === 'idle') {
      return true;
    }
    // If recording, can only open the same encounter
    return session.encounter.id === encounterId;
  }, [session]);

  // Set transcription callback
  const setOnTranscriptionComplete = useCallback((callback: ((transcription: string, generatedNote: string) => void) | undefined) => {
    onTranscriptionCompleteRef.current = callback;
  }, []);

  // Get transcription job for a specific encounter
  const getTranscriptionJob = useCallback((encounterId: string): TranscriptionJob | undefined => {
    return transcriptionJobs.get(encounterId);
  }, [transcriptionJobs]);

  // Dismiss/acknowledge a completed job
  const dismissTranscriptionJob = useCallback((encounterId: string): void => {
    // Stop any polling
    const interval = pollingIntervalsRef.current.get(encounterId);
    if (interval) {
      clearInterval(interval);
      pollingIntervalsRef.current.delete(encounterId);
    }

    // Remove from state
    setTranscriptionJobs(prev => {
      const updated = new Map(prev);
      updated.delete(encounterId);
      return updated;
    });
  }, []);

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      // Clear all polling intervals
      for (const interval of pollingIntervalsRef.current.values()) {
        clearInterval(interval);
      }
      pollingIntervalsRef.current.clear();
    };
  }, []);

  // Handle browser close/refresh while recording
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
      if (session && session.state !== 'idle') {
        e.preventDefault();
        e.returnValue = 'You have a recording in progress. Are you sure you want to leave?';
        return e.returnValue;
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [session]);

  const value = useMemo(
    (): AIRecordingContextValue => ({
      session,
      aiSettings,
      isSupported,
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      cancelRecording,
      canOpenEncounter,
      setOnTranscriptionComplete,
      transcriptionJobs,
      getTranscriptionJob,
      dismissTranscriptionJob,
    }),
    [
      session,
      aiSettings,
      isSupported,
      startRecording,
      stopRecording,
      pauseRecording,
      resumeRecording,
      cancelRecording,
      canOpenEncounter,
      setOnTranscriptionComplete,
      transcriptionJobs,
      getTranscriptionJob,
      dismissTranscriptionJob,
    ]
  );

  return (
    <AIRecordingContext.Provider value={value}>
      {children}
    </AIRecordingContext.Provider>
  );
}

export function useAIRecording(): AIRecordingContextValue {
  const context = useContext(AIRecordingContext);
  if (!context) {
    throw new Error('useAIRecording must be used within an AIRecordingProvider');
  }
  return context;
}
